(function () {
  'use strict';

  /* ==========================================================
     CONFIG
  ========================================================== */

  const SHOP_ROOT =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  const CART_URL =
    SHOP_ROOT + 'cart';

  const SECTION_ID =
    'free-gift-selector';

  const GIFT_PRODUCT_ID =
    8115404996847;

  const CAMPAIGN_ID =
    'pedicure-chairs-drexel-2026';

  const FREE_GIFT_PROPERTY =
    '_free_gift';

  const CAMPAIGN_PROPERTY =
    '_free_gift_campaign';

  const ALLOWED_GIFT_VARIANTS =
    new Set([
      44095990235375, // White
      44095990202607, // Black
      44095990268143, // Pink
      44095990300911  // Grey
    ]);

  /* ==========================================================
     STATE
  ========================================================== */

  let syncRunning =
    false;

  let mutationRunning =
    false;

  let syncTimer =
    null;

  let dismissed =
    false;

  let popupAutoOpened =
    false;

  let previousEligible =
    false;

  let redirectingToCart =
    false;

  /* ==========================================================
     HELPERS
  ========================================================== */

  function sleep(ms) {
    return new Promise(
      function (resolve) {
        window.setTimeout(
          resolve,
          ms
        );
      }
    );
  }

  function getGiftRoot() {
    return document.querySelector(
      '[data-free-gift-root]'
    );
  }

  function getGiftModal() {
    return document.querySelector(
      '[data-free-gift-modal]'
    );
  }

  function isGiftModalOpen() {
    const modal =
      getGiftModal();

    return Boolean(
      modal &&
      !modal.hidden
    );
  }

  function getState() {
    const root =
      getGiftRoot();

    if (!root) {
      return null;
    }

    return {
      eligible:
        root.dataset.eligible ===
        'true',

      hasGift:
        root.dataset.hasGift ===
        'true',

      productId:
        Number(
          root.dataset.giftProductId ||
          0
        ),

      campaignId:
        String(
          root.dataset.campaignId ||
          ''
        )
    };
  }

  function isCartPage() {
    const cartPath =
      new URL(
        CART_URL,
        window.location.origin
      ).pathname.replace(
        /\/+$/,
        ''
      );

    const currentPath =
      window.location.pathname.replace(
        /\/+$/,
        ''
      );

    return (
      currentPath ===
      cartPath
    );
  }

  function goToCart() {
    if (redirectingToCart) {
      return;
    }

    redirectingToCart =
      true;

    window.location.assign(
      CART_URL
    );
  }

  /* ==========================================================
     REQUEST
  ========================================================== */

  async function requestJSON(
    url,
    options
  ) {
    const settings =
      Object.assign(
        {
          credentials:
            'same-origin'
        },
        options || {}
      );

    const response =
      await fetch(
        url,
        settings
      );

    if (!response.ok) {
      let message =
        'Request failed.';

      try {
        const data =
          await response.json();

        message =
          data.description ||
          data.message ||
          message;
      } catch (error) {
        message =
          'Request failed (' +
          response.status +
          ').';
      }

      throw new Error(
        message
      );
    }

    return response.json();
  }

  /* ==========================================================
     CART
  ========================================================== */

  async function getCart() {
    return requestJSON(
      SHOP_ROOT + 'cart.js',
      {
        method: 'GET',

        headers: {
          Accept:
            'application/json'
        }
      }
    );
  }

  async function changeCartLine(
    lineKey,
    quantity
  ) {
    return requestJSON(
      SHOP_ROOT +
      'cart/change.js',
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json',

          Accept:
            'application/json'
        },

        body:
          JSON.stringify({
            id: lineKey,
            quantity: quantity
          })
      }
    );
  }

  /* ==========================================================
     ADD PROMOTIONAL GIFT
  ========================================================== */

  async function addGiftVariant(
    variantId
  ) {
    variantId =
      Number(
        variantId
      );

    if (
      !ALLOWED_GIFT_VARIANTS.has(
        variantId
      )
    ) {
      throw new Error(
        'Invalid free gift color.'
      );
    }

    return requestJSON(
      SHOP_ROOT +
      'cart/add.js',
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json',

          Accept:
            'application/json'
        },

        body:
          JSON.stringify({
            items: [
              {
                id:
                  variantId,

                quantity:
                  1,

                properties: {
                  [FREE_GIFT_PROPERTY]:
                    'true',

                  [CAMPAIGN_PROPERTY]:
                    CAMPAIGN_ID
                }
              }
            ]
          })
      }
    );
  }

  /* ==========================================================
     IDENTIFY PROMOTIONAL GIFT ONLY

     A normal full-price DREXEL is NOT a gift.
     Product ID alone never qualifies.
  ========================================================== */

  function isCampaignGift(
    item
  ) {
    if (!item) {
      return false;
    }

    if (
      Number(
        item.product_id
      ) !==
      GIFT_PRODUCT_ID
    ) {
      return false;
    }

    if (!item.properties) {
      return false;
    }

    const freeGift =
      String(
        item.properties[
          FREE_GIFT_PROPERTY
        ] || ''
      ) ===
      'true';

    const correctCampaign =
      String(
        item.properties[
          CAMPAIGN_PROPERTY
        ] || ''
      ) ===
      CAMPAIGN_ID;

    return (
      freeGift &&
      correctCampaign
    );
  }

  function getGiftLines(
    cart
  ) {
    if (
      !cart ||
      !Array.isArray(
        cart.items
      )
    ) {
      return [];
    }

    return cart.items.filter(
      isCampaignGift
    );
  }

  /* ==========================================================
     SAFE REMOVE

     IMPORTANT:
     Always fetch fresh cart before removing.
     Never reuse an old line key after cart mutation.
  ========================================================== */

  async function removeAllCampaignGiftsSafely() {
    let removed =
      0;

    for (
      let attempt = 0;
      attempt < 10;
      attempt++
    ) {
      const cart =
        await getCart();

      const giftLines =
        getGiftLines(
          cart
        );

      if (
        giftLines.length === 0
      ) {
        return removed;
      }

      const giftToRemove =
        giftLines[0];

      await changeCartLine(
        giftToRemove.key,
        0
      );

      removed++;

      await sleep(
        40
      );
    }

    return removed;
  }

  async function keepOnlySelectedGiftSafely(
    selectedVariantId
  ) {
    selectedVariantId =
      Number(
        selectedVariantId
      );

    for (
      let attempt = 0;
      attempt < 10;
      attempt++
    ) {
      const cart =
        await getCart();

      const gifts =
        getGiftLines(
          cart
        );

      if (
        gifts.length === 0
      ) {
        return false;
      }

      const selectedGift =
        gifts.find(
          function (item) {
            return (
              Number(
                item.variant_id
              ) ===
              selectedVariantId
            );
          }
        );

      if (!selectedGift) {
        return false;
      }

      const extraGift =
        gifts.find(
          function (item) {
            return (
              item.key !==
              selectedGift.key
            );
          }
        );

      if (extraGift) {
        await changeCartLine(
          extraGift.key,
          0
        );

        await sleep(
          40
        );

        continue;
      }

      if (
        Number(
          selectedGift.quantity
        ) !== 1
      ) {
        await changeCartLine(
          selectedGift.key,
          1
        );

        await sleep(
          40
        );
      }

      return true;
    }

    return false;
  }

  async function normalizeCampaignGiftsSafely() {
    const cart =
      await getCart();

    const gifts =
      getGiftLines(
        cart
      );

    if (
      gifts.length === 0
    ) {
      return false;
    }

    const variantToKeep =
      Number(
        gifts[0].variant_id
      );

    return keepOnlySelectedGiftSafely(
      variantToKeep
    );
  }

  /* ==========================================================
     REFRESH SECTION
  ========================================================== */

  async function refreshGiftSection() {
    const currentRoot =
      getGiftRoot();

    if (!currentRoot) {
      return null;
    }

    const url =
      SHOP_ROOT +
      'cart?section_id=' +
      encodeURIComponent(
        SECTION_ID
      );

    const response =
      await fetch(
        url,
        {
          credentials:
            'same-origin',

          headers: {
            'X-Requested-With':
              'XMLHttpRequest'
          }
        }
      );

    if (!response.ok) {
      throw new Error(
        'Unable to refresh free gift selector.'
      );
    }

    const html =
      await response.text();

    const parser =
      new DOMParser();

    const parsed =
      parser.parseFromString(
        html,
        'text/html'
      );

    const newRoot =
      parsed.querySelector(
        '[data-free-gift-root]'
      );

    if (!newRoot) {
      throw new Error(
        'Free gift selector section was not found.'
      );
    }

    const refreshedModal =
      newRoot.querySelector(
        '[data-free-gift-modal]'
      );

    if (refreshedModal) {
      refreshedModal.hidden =
        true;
    }

    currentRoot.replaceWith(
      newRoot
    );

    return newRoot;
  }

  /* ==========================================================
     MODAL
  ========================================================== */

  function openGiftModal(
    forceOpen
  ) {
    const modal =
      getGiftModal();

    if (!modal) {
      return;
    }

    if (!modal.hidden) {
      return;
    }

    if (!forceOpen) {
      if (dismissed) {
        return;
      }

      if (popupAutoOpened) {
        return;
      }

      popupAutoOpened =
        true;
    }

    modal.hidden =
      false;

    document.documentElement.classList.add(
      'free-gift-modal-open'
    );
  }

  function closeGiftModal(
    customerDismissed
  ) {
    const modal =
      getGiftModal();

    if (modal) {
      modal.hidden =
        true;
    }

    document.documentElement.classList.remove(
      'free-gift-modal-open'
    );

    if (customerDismissed) {
      dismissed =
        true;
    }
  }

  /* ==========================================================
     EXPANSE CART
  ========================================================== */

  function rebuildExpanseCart() {
    document.dispatchEvent(
      new CustomEvent(
        'cart:build'
      )
    );
  }

  /* ==========================================================
     ELIGIBILITY SESSION
  ========================================================== */

  function updateEligibilitySession(
    eligible
  ) {
    if (
      eligible &&
      !previousEligible
    ) {
      dismissed =
        false;

      popupAutoOpened =
        false;
    }

    if (
      !eligible &&
      previousEligible
    ) {
      dismissed =
        false;

      popupAutoOpened =
        false;
    }

    previousEligible =
      eligible;
  }

  /* ==========================================================
     MAIN SYNC
  ========================================================== */

  async function syncFreeGift() {
    if (
      syncRunning ||
      mutationRunning ||
      redirectingToCart
    ) {
      return;
    }

    if (
      isGiftModalOpen()
    ) {
      return;
    }

    syncRunning =
      true;

    try {
      /*
       * FASTEST first request:
       * Liquid section determines eligibility.
       */

      await refreshGiftSection();

      let state =
        getState();

      if (!state) {
        return;
      }

      updateEligibilitySession(
        state.eligible
      );

      /*
       * Show popup immediately after eligibility is known.
       * Do not wait for cart.js.
       */

      if (
        state.eligible &&
        !state.hasGift
      ) {
        openGiftModal(
          false
        );
      }

      /*
       * Background cart validation.
       */

      const cart =
        await getCart();

      const giftLines =
        getGiftLines(
          cart
        );

      if (!state.eligible) {
        if (
          giftLines.length > 0
        ) {
          mutationRunning =
            true;

          const removed =
            await removeAllCampaignGiftsSafely();

          mutationRunning =
            false;

          if (
            removed > 0
          ) {
            await sleep(
              50
            );

            rebuildExpanseCart();

            await refreshGiftSection();
          }
        }

        closeGiftModal(
          false
        );

        return;
      }

      if (
        giftLines.length > 1 ||
        (
          giftLines.length === 1 &&
          Number(
            giftLines[0].quantity
          ) !== 1
        )
      ) {
        mutationRunning =
          true;

        await normalizeCampaignGiftsSafely();

        mutationRunning =
          false;

        await sleep(
          50
        );

        rebuildExpanseCart();

        await refreshGiftSection();

        state =
          getState();
      }

      if (
        state &&
        state.hasGift
      ) {
        closeGiftModal(
          false
        );
      }

    } catch (error) {
      console.error(
        '[Free Gift]',
        error
      );
    } finally {
      mutationRunning =
        false;

      syncRunning =
        false;
    }
  }

  /* ==========================================================
     SELECT / CHANGE GIFT COLOR
  ========================================================== */

  async function selectGiftVariant(
    button
  ) {
    if (
      mutationRunning ||
      syncRunning ||
      redirectingToCart
    ) {
      return;
    }

    const variantId =
      Number(
        button.dataset
          .freeGiftVariant
      );

    if (
      !variantId ||
      !ALLOWED_GIFT_VARIANTS.has(
        variantId
      )
    ) {
      return;
    }

    const alreadyOnCartPage =
      isCartPage();

    /*
     * Close immediately.
     */

    dismissed =
      false;

    popupAutoOpened =
      true;

    closeGiftModal(
      false
    );

    mutationRunning =
      true;

    try {
      /*
       * Verify qualification in background.
       */

      await refreshGiftSection();

      const state =
        getState();

      if (
        !state ||
        !state.eligible
      ) {
        throw new Error(
          'Your cart is no longer eligible for this free gift.'
        );
      }

      const cart =
        await getCart();

      const existingGifts =
        getGiftLines(
          cart
        );

      /*
       * Same promotional gift already selected.
       */

      if (
        existingGifts.length === 1 &&
        Number(
          existingGifts[0].variant_id
        ) ===
          variantId &&
        Number(
          existingGifts[0].quantity
        ) === 1
      ) {
        if (
          !alreadyOnCartPage
        ) {
          mutationRunning =
            false;

          goToCart();

          return;
        }

        await refreshGiftSection();

        return;
      }

      /*
       * Add selected gift.
       */

      await addGiftVariant(
        variantId
      );

      /*
       * Shopify discount/cart recalculation may change keys.
       * Fetch fresh cart again before removing anything.
       */

      await sleep(
        40
      );

      const success =
        await keepOnlySelectedGiftSafely(
          variantId
        );

      if (!success) {
        throw new Error(
          'Unable to confirm the selected free gift.'
        );
      }

      /*
       * If customer is not already on /cart,
       * redirect immediately after gift is confirmed.
       */

      if (
        !alreadyOnCartPage
      ) {
        mutationRunning =
          false;

        goToCart();

        return;
      }

      /*
       * Already on cart page:
       * stay here and refresh UI.
       */

      await sleep(
        50
      );

      rebuildExpanseCart();

      await refreshGiftSection();

    } catch (error) {
      console.error(
        '[Free Gift]',
        error
      );

      try {
        await refreshGiftSection();
      } catch (refreshError) {
        console.error(
          '[Free Gift]',
          refreshError
        );
      }

      openGiftModal(
        true
      );

      window.alert(
        error.message ||
        'Unable to add your free gift. Please try again.'
      );

    } finally {
      mutationRunning =
        false;

      if (
        !redirectingToCart &&
        isCartPage()
      ) {
        scheduleSync(
          120
        );
      }
    }
  }

  /* ==========================================================
     DEBOUNCE
  ========================================================== */

  function scheduleSync(
    delay
  ) {
    window.clearTimeout(
      syncTimer
    );

    syncTimer =
      window.setTimeout(
        function () {
          syncFreeGift();
        },
        typeof delay ===
        'number'
          ? delay
          : 20
      );
  }

  /* ==========================================================
     CLICK
  ========================================================== */

  document.addEventListener(
    'click',
    function (event) {
      const variantButton =
        event.target.closest(
          '[data-free-gift-variant]'
        );

      if (variantButton) {
        event.preventDefault();

        selectGiftVariant(
          variantButton
        );

        return;
      }

      const openButton =
        event.target.closest(
          '[data-free-gift-open]'
        );

      if (openButton) {
        event.preventDefault();

        openGiftModal(
          true
        );

        return;
      }

      const closeButton =
        event.target.closest(
          '[data-free-gift-close]'
        );

      if (closeButton) {
        event.preventDefault();

        closeGiftModal(
          true
        );
      }
    }
  );

  /* ==========================================================
     ESC
  ========================================================== */

  document.addEventListener(
    'keydown',
    function (event) {
      if (
        event.key !==
        'Escape'
      ) {
        return;
      }

      if (
        isGiftModalOpen()
      ) {
        closeGiftModal(
          true
        );
      }
    }
  );

  /* ==========================================================
     EXPANSE EVENTS
  ========================================================== */

  document.addEventListener(
    'ajaxProduct:added',
    function () {
      if (
        mutationRunning ||
        redirectingToCart
      ) {
        return;
      }

      scheduleSync(
        0
      );
    }
  );

  document.addEventListener(
    'cart:updated',
    function () {
      if (
        mutationRunning ||
        redirectingToCart
      ) {
        return;
      }

      if (
        isGiftModalOpen()
      ) {
        return;
      }

      scheduleSync(
        30
      );
    }
  );

  document.addEventListener(
    'page:loaded',
    function () {
      if (
        mutationRunning ||
        redirectingToCart
      ) {
        return;
      }

      scheduleSync(
        40
      );
    }
  );

  /* ==========================================================
     NORMAL LOAD
  ========================================================== */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        scheduleSync(
          60
        );
      }
    );
  } else {
    scheduleSync(
      60
    );
  }

})();
